import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export const PERIOD_OPTIONS = [
    { value: '24h', label: '24時間' },
    { value: '7d', label: '7日間' },
    { value: '28d', label: '28日間' },
    { value: '3m', label: '3カ月' },
    { value: '1y', label: '1年' },
] as const;

export const TYPE_OPTIONS = [
    { value: 'page', label: 'ページ（商品と作家）', simple: 'ページ' },
    { value: 'referrer', label: 'リファラ（参照元）', simple: 'リファラ' },
] as const;

export const INTERVAL_OPTIONS = [
    { value: '1h', label: '1時間ごと' },
    { value: '1d', label: '1日ごと' },
    { value: '1w', label: '1週間ごと' },
    { value: '1m', label: '1カ月ごと' },
] as const;

const AVAILABLE_INTERVALS: Record<PeriodValue, IntervalValue[]> = {
    '24h': ['1h'],
    '7d': ['1d'],
    '28d': ['1d', '1w'],
    '3m': ['1w', '1m'],
    '1y': ['1m'],
};

export type PeriodValue = (typeof PERIOD_OPTIONS)[number]['value'];
export type TypeValue = (typeof TYPE_OPTIONS)[number]['value'];
export type IntervalValue = (typeof INTERVAL_OPTIONS)[number]['value'];

export type FilterItem = {
    key:string;
    name:string;
    imgId?:number;
}
export type FilterValue = {
    period: PeriodValue;
    type: TypeValue;
    interval: IntervalValue;
    filters: {
        items: {[key:string]:FilterItem},
        summarys: {[key:string]:FilterItem},
    };
};

export const resolveSummaryKey = (
    summary: string | undefined,
    bank: {[key:string]: string},
    createIfMissing: boolean
): { summaryKey: string; summaryValue: string } | null => {
    const summaryValue = summary ?? "";
    let summaryKey = summary && summary!=="" ? summary : "none";

    if (summaryValue.startsWith("http://") || summaryValue.startsWith("https://")) {
        const existingKey = Object.keys(bank).find((k) => bank[k] === summaryValue);
        if (existingKey) {
            summaryKey = existingKey;
        } else if (createIfMissing) {
            const urlCount = Object.keys(bank).length + 1;
            summaryKey = `URL${urlCount}`;
            bank[summaryKey] = summaryValue;
        } else {
            return null;
        }
    }

    return { summaryKey, summaryValue };
};


interface AccessLogSelectorsProps {
    value: FilterValue;
    onChange: (next: FilterValue) => void;
}

const AccessLogSelectors: React.FC<AccessLogSelectorsProps> = ({ value, onChange }) => {
    const [anchorTypeEl, setAnchorTypeEl] = useState<null | HTMLElement>(null);
    const [anchorIntervalEl, setAnchorIntervalEl] = useState<null | HTMLElement>(null);

    const typeOpen = Boolean(anchorTypeEl);
    const intervalOpen = Boolean(anchorIntervalEl);

    const typeButtonWidth = anchorTypeEl?.getBoundingClientRect().width;
    const intervalButtonWidth = anchorIntervalEl?.getBoundingClientRect().width;

    const selectedType = useMemo(
        () => TYPE_OPTIONS.find((x) => x.value === value.type),
        [value.type]
    );
    const selectedInterval = useMemo(
        () => INTERVAL_OPTIONS.find((x) => x.value === value.interval),
        [value.interval]
    );

    const getPeriodButtonSx = (period: PeriodValue) => ({
        fontSize: { xs: '0.7rem', sm: '0.9rem' },
        whiteSpace: 'nowrap',
        ...(value.period === period && {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
        }),
    });

    return (
        <>
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'flex-start',
                marginBottom: '1rem',
                gap: '0.5rem',
            }}
        >
            <ButtonGroup variant="outlined" aria-label="period">
                {PERIOD_OPTIONS.map((option) => (
                    <Button
                        key={option.value}
                        value={option.value}
                        onClick={(e) => {
                            const period = e.currentTarget.value as PeriodValue;
                            const interval = AVAILABLE_INTERVALS[period].includes(value.interval)
                                ? value.interval
                                : AVAILABLE_INTERVALS[period][0];
                            onChange({ ...value, period: period, interval: interval })
                        }}
                        startIcon={
                            value.period === option.value
                                ? <CheckIcon sx={{ width: { xs: '0.8rem', sm: '1rem' } }} />
                                : undefined
                        }
                        sx={getPeriodButtonSx(option.value)}
                    >
                        {option.label}
                    </Button>
                ))}
            </ButtonGroup>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '0.5rem',
                    flex: { md: 1 },
                    minWidth: 0,
                }}
            >
                {/* <ButtonGroup variant="outlined" aria-label="type">
                    <Button
                        id="type-button"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, whiteSpace: 'nowrap' }}
                        endIcon={<ArrowDropDownIcon />}
                        aria-controls={typeOpen ? 'type-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={typeOpen ? 'true' : undefined}
                        onClick={(e) => setAnchorTypeEl(e.currentTarget)}
                    >
                        集計タイプ：{selectedType?.simple}
                    </Button>
                </ButtonGroup> */}

                <ButtonGroup variant="outlined" aria-label="interval" sx={{ ml: { md: 'auto' } }}>
                    <Button
                        id="interval-button"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, whiteSpace: 'nowrap' }}
                        endIcon={<ArrowDropDownIcon />}
                        aria-controls={intervalOpen ? 'interval-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={intervalOpen ? 'true' : undefined}
                        onClick={(e) => setAnchorIntervalEl(e.currentTarget)}
                    >
                        {selectedInterval?.label}
                    </Button>
                </ButtonGroup>
            </Box>

            <Menu
                id="type-menu"
                anchorEl={anchorTypeEl}
                open={typeOpen}
                onClose={() => setAnchorTypeEl(null)}
                slotProps={{ paper: { sx: { width: typeButtonWidth } } }}
                MenuListProps={{ 'aria-labelledby': 'type-button' }}
            >
                {TYPE_OPTIONS.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => {
                            onChange({ ...value, type: option.value });
                            setAnchorTypeEl(null);
                        }}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, whiteSpace: 'nowrap' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                id="interval-menu"
                anchorEl={anchorIntervalEl}
                open={intervalOpen}
                onClose={() => setAnchorIntervalEl(null)}
                slotProps={{ paper: { sx: { width: intervalButtonWidth } } }}
                MenuListProps={{ 'aria-labelledby': 'interval-button' }}
            >
                {INTERVAL_OPTIONS.filter((option) =>
                    AVAILABLE_INTERVALS[value.period].includes(option.value)
                ).map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => {
                            onChange({ ...value, interval: option.value });
                            setAnchorIntervalEl(null);
                        }}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, whiteSpace: 'nowrap' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
        </>
    );
};

export default AccessLogSelectors;